import { z } from "zod";
import { estiloIdsSchema } from "./estilos-de-arte.js";
import { COPIAS_MAXIMAS, areaUtilEmPx, enquadramentoInicial, planoDaCelula, } from "./impressao.js";
/**
 * O marcador de livro: o que cabe numa folha, e a conferência do que a IA escreveu.
 *
 * Tudo aqui é puro (sem I/O), como a geometria da impressão: a folha que o servidor
 * monta e o que a tela mostra saem das mesmas contas, e o texto que a IA devolve
 * é conferido por uma regra testável, não por um olhar.
 */
/** Marcadores por folha: três faixas lado a lado no postal da SELPHY. */
export const MARCADORES_POR_FOLHA = 3;
/**
 * A proporção pedida ao modelo para cada marcador. É mais alta que a arte guardada
 * (0,25 contra 0,2254) e que a faixa da folha (0,2216): o servidor corta um pouco dos lados,
 * e o prompt reserva a margem para o corte não pegar no texto.
 *
 * Só os modelos que declaram `1:4` em `supported_parameters.aspect_ratio` servem;
 * os outros recusam com 400.
 */
export const PROPORCAO_DO_MARCADOR = "1:4";
/**
 * Em 1:4, a `1K` da configuração entrega 256 px de largura — a faixa pede 394.
 * `2K` entrega 512 ou mais, conforme o modelo.
 */
export const RESOLUCAO_DO_MARCADOR = "2K";
/**
 * O tamanho da arte de cada marcador, como fica guardada: 394 × 1748 px, o recorte do 1:4 da
 * IA (cortando um pouco dos lados). É a arte inteira de uma faixa; a folha a encaixa na área útil
 * do papel, num espaço um pouco menor (`faixasDaFolha`), com ~1,7% de ajuste.
 */
export const FAIXA_DE_ARTE = { largura: 394, altura: 1748 };
/**
 * As três faixas em pixels da folha, **dentro da área útil do papel** (`AREA_UTIL`, 93,6 × 141 mm,
 * no meio do postal): 31,2 × 141 mm cada. A margem em volta fica em branco, porque o picotado
 * a come. Na primeira versão as faixas ocupavam a página inteira: as das pontas perderam as
 * laterais e o rodapé caiu na aba do papel.
 *
 * 1105 px (a largura útil) não divide por 3: a primeira faixa leva 369 e as outras duas 368.
 */
export function faixasDaFolha() {
    const area = areaUtilEmPx();
    const faixas = [];
    for (let i = 0; i < MARCADORES_POR_FOLHA; i++) {
        const inicio = Math.ceil((i * area.largura) / MARCADORES_POR_FOLHA);
        const fim = i === MARCADORES_POR_FOLHA - 1
            ? area.largura
            : Math.ceil(((i + 1) * area.largura) / MARCADORES_POR_FOLHA);
        faixas.push({ x: area.x + inicio, y: area.y, largura: fim - inicio, altura: area.altura });
    }
    return faixas;
}
/**
 * Onde passam as guias de corte: a posição x de cada emenda entre faixas. O servidor as
 * desenha pontilhadas, por cima da arte, porque não há vão onde pô-las — e só do topo ao pé
 * das faixas, não da folha: fora da área útil não há arte para guiar.
 */
export function guiasDeCorte() {
    return faixasDaFolha()
        .slice(1)
        .map((f) => f.x);
}
/**
 * Como a imagem da IA cobre a faixa: preenche a faixa inteira e corta o que sobra,
 * centrado — nos dois lados, nunca em cima ou embaixo, porque a imagem é mais
 * estreita que a faixa só na proporção.
 */
export function planoDaFaixa(imagem, faixa) {
    return planoDaCelula(enquadramentoInicial(imagem, faixa, "preencher"), imagem, faixa);
}
/**
 * Quanto da largura da imagem se perde no corte, em fração (0 a 1). O prompt
 * reserva 8% de margem em cada borda; se o corte passar disso, o texto pode ser
 * cortado e o marcador precisa de aviso.
 */
export function fracaoCortadaDosLados(imagem, faixa) {
    const escala = Math.max(faixa.largura / imagem.largura, faixa.altura / imagem.altura);
    const larguraEscalada = imagem.largura * escala;
    return Math.max(0, (larguraEscalada - faixa.largura) / larguraEscalada);
}
// ─── Os campos ────────────────────────────────────────────────────────────────
/**
 * Tira o que atrapalha um texto que vai para o prompt e para o papel: espaços
 * repetidos e as aspas « » que delimitam o texto no prompt.
 */
const limpar = (t) => t
    .replace(/[«»]/g, "")
    // eslint-disable-next-line no-control-regex -- caracteres de controle não têm lugar num marcador
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, "")
    .replace(/[ \t]+/g, " ")
    .trim();
/**
 * Vazio vira ausente, e o `.optional()` por último é o que deixa a chave opcional no
 * tipo de saída — com a transformação por último, quem monta o objeto à mão (um teste,
 * uma migração de dados) teria que escrever todas as chaves.
 */
const linhaOpcional = (max) => z
    .string()
    .max(max)
    .transform(limpar)
    .transform((t) => (t === "" ? undefined : t))
    .optional();
/**
 * O que o operador preenche. Só o título é obrigatório; o resto que ficar vazio
 * sai do prompt, junto com a instrução de não escrever outro texto no lugar.
 */
export const camposDoMarcadorSchema = z.object({
    titulo: z.string().min(1).max(50).transform(limpar).pipe(z.string().min(1)),
    subtitulo: linhaOpcional(60),
    frase: linhaOpcional(80),
    /**
     * Em geral os sites, uma ou duas linhas. Cada linha vira uma entrada na
     * conferência, para a quebra que a IA fizer não contar como erro.
     */
    rodape: z
        .string()
        .max(100)
        .transform((t) => t
        .split("\n")
        .map(limpar)
        .filter((l) => l !== "")
        .slice(0, 2)
        .join("\n"))
        .transform((t) => (t === "" ? undefined : t))
        .optional(),
});
/** A observação livre do operador à IA ("tons de Páscoa"). */
export const observacaoDoMarcadorSchema = z
    .string()
    .max(300)
    .transform(limpar)
    .transform((t) => (t === "" ? undefined : t))
    .optional();
/**
 * O maior texto de direção de arte de uma faixa. Um estilo do catálogo com motivo, luz e as
 * cores da capa chega a ~700 caracteres (o pior caso, "noite", a 703), e a tela deixa o operador editá-lo.
 */
export const TAMANHO_MAXIMO_DO_ESTILO = 800;
/** As três direções de arte de um conjunto: uma por marcador, texto livre. */
export const estilosDoMarcadorSchema = z
    .array(z.string().transform(limpar).pipe(z.string().min(10).max(TAMANHO_MAXIMO_DO_ESTILO)))
    .length(MARCADORES_POR_FOLHA);
/**
 * Cada texto que a arte tem que mostrar, um por entrada, na ordem de cima para
 * baixo: título, subtítulo, frase e cada linha do rodapé.
 */
export function textosEsperados(campos) {
    return [
        campos.titulo,
        ...(campos.subtitulo ? [campos.subtitulo] : []),
        ...(campos.frase ? [campos.frase] : []),
        ...(campos.rodape ? campos.rodape.split("\n") : []),
    ];
}
// ─── A conferência do texto ───────────────────────────────────────────────────
/**
 * Deixa um texto comparável: minúsculas, e tudo que não é letra ou número vira
 * espaço único. **O acento fica**: no ensaio o Qwen escreveu «Quem le» e uma
 * comparação sem acento deixaria passar o erro, que é justamente o que a IA mais
 * comete. A visão lê o acento (leu «le» e «lê» certo), então a regra pode exigi-lo.
 */
export function normalizarTexto(texto) {
    return texto
        .normalize("NFC")
        .toLocaleLowerCase("pt-BR")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
}
const semAcento = (t) => t.normalize("NFD").replace(/\p{M}+/gu, "");
const compactar = (t) => t.normalize("NFC").replace(/\s+/g, " ").trim();
const escaparRegex = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/**
 * O texto pedido aparece na leitura **tal como foi digitado** — mesma caixa, mesma
 * pontuação? A visão às vezes não lê o ponto final, e isso não é erro da arte: sem a
 * pontuação do fim, vale se o que vem depois é espaço ou o fim da linha. Já um "!" no lugar do "."
 * é leitura e é erro: o que vem depois do texto é o "!".
 */
function aparecePeloMenosComoDigitado(esperado, lido) {
    const e = compactar(esperado);
    if (lido.includes(e))
        return true;
    const nucleo = e.replace(/[.!?…,;:]+$/u, "");
    if (nucleo === e || nucleo === "")
        return false;
    return new RegExp(`${escaparRegex(nucleo)}(?=\\s|$)`).test(lido);
}
/**
 * Confere o que a visão leu da faixa contra o que foi pedido. Cada texto esperado
 * tem que aparecer inteiro, em palavras inteiras, no que foi lido — as linhas
 * lidas se juntam, então um título quebrado em duas linhas pela IA vale. O que
 * sobra na leitura não conta: a capa do livro tem texto pequeno que a IA inventa e
 * a visão lê, e não é o texto do operador.
 *
 * Em dois degraus: as **letras e os acentos** têm de bater (senão `acento` ou
 * `ausente`), e depois a **caixa e a pontuação** (senão `forma`). A IA muda uma e outra
 * por conta própria — "Ainda não é o FIM", "Quem lê, Atenda!" — e o que o operador
 * digitou é o que vai para o papel.
 *
 * Lista vazia: tudo conferido.
 */
export function conferirTexto(esperados, lidas) {
    const lido = ` ${normalizarTexto(lidas.join(" "))} `;
    const lidoSemAcento = semAcento(lido);
    const lidoBruto = ` ${compactar(lidas.join(" "))} `;
    const divergencias = [];
    for (const esperado of esperados) {
        const n = normalizarTexto(esperado);
        if (n === "")
            continue;
        if (lido.includes(` ${n} `)) {
            if (!aparecePeloMenosComoDigitado(esperado, lidoBruto)) {
                divergencias.push({ esperado, tipo: "forma" });
            }
            continue;
        }
        divergencias.push({
            esperado,
            tipo: lidoSemAcento.includes(` ${semAcento(n)} `) ? "acento" : "ausente",
        });
    }
    return divergencias;
}
/**
 * Saíram aspas « » na arte? São os delimitadores do prompt, e a IA às vezes os
 * desenha junto do texto. O operador não as digita (`limpar` as tira), então qualquer
 * uma na leitura é vazamento.
 */
export function aspasNaArte(lidas) {
    return lidas.some((l) => /[«»]/.test(l));
}
/** A divergência em português, para o aviso da faixa. */
export function descreverDivergencia(d) {
    switch (d.tipo) {
        case "acento":
            return `Falta ou sobra um acento em «${d.esperado}».`;
        case "forma":
            return `As maiúsculas ou a pontuação de «${d.esperado}» saíram diferentes do digitado.`;
        default:
            // A leitura não o achou: ausente, errado ou ilegível (escuro sobre fundo cheio). Só olhando se sabe.
            return `Não consegui ler «${d.esperado}» na arte: pode ter saído errado ou ilegível. Confira.`;
    }
}
export const AVISO_DE_ASPAS = "Saíram aspas « » na arte: elas não fazem parte do texto.";
/**
 * Lê a resposta da visão ao pedido de leitura (`{"linhas":["..."]}`). Aceita
 * cerca de markdown e frase em volta. Resposta sem aproveitamento é `null`, e não
 * exceção: sem leitura, a faixa fica sem conferência, e não sem arte.
 */
export function lerLeituraDoTexto(resposta) {
    const ini = resposta.indexOf("{");
    const fim = resposta.lastIndexOf("}");
    if (ini < 0 || fim <= ini)
        return null;
    try {
        const cru = JSON.parse(resposta.slice(ini, fim + 1));
        const r = z.object({ linhas: z.array(z.string()) }).safeParse(cru);
        return r.success ? r.data.linhas : null;
    }
    catch {
        return null;
    }
}
/**
 * O que a IA pode fazer com a pose das pessoas. A IA redesenha as pessoas de qualquer jeito
 * (não há como recolar a foto), então a pose pode ser **pedida**: `manter` a da foto, ou
 * `ajustar` ao marcador — de frente, segurando o livro, por exemplo —, o que libera a pose e
 * trava o resto da identidade.
 */
export const POSES_DO_MARCADOR = ["manter", "ajustar"];
/**
 * As versões da foto que o marcador aceita de referência: a peça ou a foto da IA. A foto
 * original (`FONTES_DA_FOTO`) vale só na impressão — o usuário a quis na folha e nos botões
 * de IA, não nos marcadores (#94) —, e por isso não entra aqui nem na coluna `marcadores.fonte`.
 */
export const FONTES_DO_MARCADOR = ["peca", "foto"];
// ─── Os pedidos da API ────────────────────────────────────────────────────────
/**
 * Gerar um conjunto de três marcadores a partir de uma foto publicada.
 *
 * As direções de arte vêm, por ordem de prioridade, de `estilos` (o operador
 * escreveu ou editou), de `estiloIds` (três do catálogo de estilos, pelo nome), do
 * `modeloId` (um modelo pré-criado) ou, sem nenhum dos três, de um modelo de texto
 * que escolhe três do catálogo e escreve o motivo — a "arte livre".
 */
export const gerarMarcadoresSchema = z.object({
    geracaoId: z.number().int().positive(),
    /**
     * A imagem que a IA recebe de referência: a peça publicada (o retrato, sem a capa da
     * vivência) ou a foto que a IA gerou, inteira. Sem ela, a peça.
     */
    fonte: z.enum(FONTES_DO_MARCADOR).optional(),
    /** Sem ela, a pose da foto. */
    pose: z.enum(POSES_DO_MARCADOR).optional(),
    /** A capa do livro, do banco de capas: as pessoas a seguram na arte. */
    capaId: z.number().int().positive().optional(),
    modeloId: z.number().int().positive().optional(),
    campos: camposDoMarcadorSchema,
    observacao: observacaoDoMarcadorSchema,
    estilos: estilosDoMarcadorSchema.optional(),
    /** Três estilos diferentes do catálogo (`ESTILOS_DE_ARTE`), na ordem das três faixas. */
    estiloIds: estiloIdsSchema.optional(),
});
/** Refazer só um marcador do conjunto, com a mesma direção ou outra. */
export const refazerMarcadorSchema = z.object({
    id: z.number().int().positive(),
    indice: z
        .number()
        .int()
        .min(0)
        .max(MARCADORES_POR_FOLHA - 1),
    /** Substitui a direção de arte só deste marcador. */
    estilo: z
        .string()
        .transform(limpar)
        .pipe(z.string().min(10).max(TAMANHO_MAXIMO_DO_ESTILO))
        .optional(),
});
/** Um modelo pré-criado: as três direções de arte e, se quiser, campos de partida. */
export const modeloDeMarcadorSchema = z.object({
    nome: z.string().trim().min(2).max(60),
    descricao: z
        .string()
        .trim()
        .max(200)
        .transform((t) => (t === "" ? undefined : t))
        .optional(),
    estilos: estilosDoMarcadorSchema,
    camposPadrao: camposDoMarcadorSchema.partial().default({}),
    observacao: observacaoDoMarcadorSchema,
});
/**
 * Imprimir a folha de um conjunto de marcadores, direto na impressora. A folha já está montada (1181 × 1748 px,
 * as três faixas dentro da área útil): o pedido só diz qual conjunto, em qual impressora e quantas folhas.
 */
export const imprimirMarcadoresSchema = z.object({
    id: z.number().int().positive(),
    impressoraId: z.number().int().positive(),
    copias: z.number().int().min(1).max(COPIAS_MAXIMAS),
});
//# sourceMappingURL=marcadores.js.map